import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import styled from "react-emotion";
import { Box, Flex, Text, A } from "./base";
import { fonts } from "src/styles";

// Banner configuration - structured for easy migration to Contentful later
const BANNER_CONFIG = {
  bannerId: 'cookie-consent-v1',
  message: 'This website uses cookies, pixels, and similar tracking technologies to improve your experience, analyze site usage, and deliver personalized content and advertising. We may share this information with our analytics, advertising, and social media partners. By selecting \'I Understand,\' or continuing to use our site, you consent to this use. For more details, see our',
  privacyPolicyText: 'Online\u00A0Privacy\u00A0Policy.',
  privacyPolicyUrl: 'https://www.fcfcu.com/mobile-privacy-policy',
  callToActionText: 'I Understand',
  backgroundColor: '#005695',
  textColor: '#ffffff',
  expirationDays: 30,
  active: true
};

const BannerContainer = styled(Box)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  background-color: ${props => props.bgColor || '#005695'};
  color: ${props => props.textColor || 'white'};
  padding: 20px 32px;

  @media print {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 16px 16px 20px 16px;
  }

  @media (max-width: 600px) {
    padding: 16px 16px 80px 16px;
  }
`;

const ContentWrapper = styled(Flex)`
  max-width: 1200px;
  margin: 0 auto;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;

  @media (min-width: 1650px) {
    flex-direction: row;
  }
`;

const MessageContent = styled(Box)`
  flex: 1;
  margin-bottom: 12px;

  @media (min-width: 1650px) {
    margin-bottom: 0;
    margin-right: 24px;
  }
`;

const MessageText = styled.div`
  margin: 0;
  line-height: 1.5;
  font-size: 14px;
  font-family: ${fonts.sansSerif};

  @media (min-width: 768px) {
    font-size: 15px;
  }
`;

const PrivacyLink = styled(A)`
  color: white;
  text-decoration: underline;
  font-size: inherit;
  font-family: ${fonts.sansSerif};
  display: inline;
  width: auto;

  &:hover {
    opacity: 0.9;
  }
`;

const CTAButton = styled.button`
  background-color: white;
  color: #005695;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  font-family: ${fonts.sansSerif};
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: 1649px) {
    align-self: flex-start;
    min-width: 200px;
  }

  @media (max-width: 600px) {
    width: 100%;
    align-self: stretch;
  }
`;

const BottomBanner = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!BANNER_CONFIG.active) return;

    const cookieName = `bottomBanner_dismissed_${BANNER_CONFIG.bannerId}`;
    const dismissed = Cookies.get(cookieName);

    if (!dismissed) {
      setIsVisible(true);
    }
  }, []);

  const handleAgree = () => {
    const cookieName = `bottomBanner_dismissed_${BANNER_CONFIG.bannerId}`;
    const isProduction = process.env.NODE_ENV === 'production';

    Cookies.set(cookieName, 'true', {
      expires: BANNER_CONFIG.expirationDays,
      path: '/',
      domain: process.env.GATSBY_COOKIE_DOMAIN || undefined,
      sameSite: 'Lax',
      secure: isProduction
    });

    setIsVisible(false);
  };

  if (!isVisible || !BANNER_CONFIG.active) return null;

  return (
    <BannerContainer
      bgColor={BANNER_CONFIG.backgroundColor}
      textColor={BANNER_CONFIG.textColor}
      data-testid="bottom-banner"
    >
      <ContentWrapper>
        <MessageContent>
          <MessageText>
            {BANNER_CONFIG.message}{' '}
            <PrivacyLink
              href={BANNER_CONFIG.privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {BANNER_CONFIG.privacyPolicyText}
            </PrivacyLink>
          </MessageText>
        </MessageContent>
        <CTAButton
          onClick={handleAgree}
          data-testid="banner-cta"
        >
          {BANNER_CONFIG.callToActionText}
        </CTAButton>
      </ContentWrapper>
    </BannerContainer>
  );
};

export default BottomBanner;



// WEBPACK FOOTER //
// ./src/components/BottomBanner.js